// steps/FuhrparkStep.web.tsx
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, Button, IconButton, ToggleButton, ToggleButtonGroup, Typography, CircularProgress } from "@mui/material";

import {
  ArrowLeft as ArrowLeftIcon,
  Check as CheckIcon,
  CircleCheck as CircleCheckIcon,
  CircleSlash as CircleSlashIcon,
  Edit3 as Edit3Icon,
  Flame as FlameIcon,
  Minus as MinusIcon,
  MountainSnow as MountainSnowIcon,
  PanelLeftOpen as PanelLeftOpenIcon,
  Plus as PlusIcon,
  RotateCw as RotateCwIcon,
  Shield as ShieldIcon,
  Trash as TrashIcon,
  Truck as TruckIcon,
  X as XIcon,
} from "lucide-react";

import { useAllowScroll } from "../AllowScrollContext";
import { FuhrparkSummary, MaterialSummary, SummaryContainer, UhrzeitSummary } from "./Summaries";
import { truckTypeLabel } from "./shared";
import { MaterialOrder, TruckInfo } from "@/components/planning/Project";
import ApiService from "@/api/ApiService";
import { ActionItem } from "../../../../../shared/sharedService/ActionItem";
import TruckInformationRow from "../../TruckInformationRow";
import { Clear } from "@mui/icons-material";

type RouteInfo = {
  loadingTime: number;
  routeTime: number;
  inversed: boolean;
};

function TitleWithWarning({ text, warning }: { text: string; warning?: boolean }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {warning ? <Box sx={{ width: 8, height: 8, borderRadius: 9999, bgcolor: "red" }} /> : null}
      <Typography variant="button" sx={{ userSelect: "none" }}>
        {text}
      </Typography>
    </Box>
  );
}

type WizardStep = "NONE" | "TRUCK_SETUP" | "TRUCK_SITEWORK" | "ANOTHER_TRUCK" | "TAKT" | "DURATION";
type WizardMode = "ADD" | "EDIT" | null;

const THERMO_TYPES = ["XX", "3A", "4A", "SA", "3A4A"];
const BORDMATIK_TYPES = ["XX", "3A", "4A", "3A4A", "2A"];
const STAHLMULDE_TYPES = ["XX", "3A", "4A", "SA", "3A4A", "2A"];
const ALLRAD_TYPES = ["XX", "SA"];

function createDefaultTruckInfo(hasArticle: boolean, _isFreiBau: boolean): TruckInfo {
  return {
    type: "XX",
    amount: 0,
    thermo: false,
    // kein Artikel -> automatisch Baustellendienst
    siteWork: hasArticle ? false : true,
    bordmatik: false,
    allrad: false,
    stahlmulde: false,
  } as TruckInfo;
}

export default function FuhrparkStep({
  order,
  jobId,
  autoCreate,
  expertMode,
  jobPosition,
  onBack,
  onSelect,
}: {
  order: MaterialOrder;
  jobId: number;
  jobPosition: any;
  autoCreate: boolean;
  expertMode: boolean;
  onBack: () => void;
  onSelect: (patch: Partial<MaterialOrder>) => void;
}) {
  const { setAllowScroll } = useAllowScroll();

  const isFreiBau = !!order.position ? order.position?.deliveryType?.name?.toLowerCase?.().includes("frei") : order.manualDAP;

  const [doFinishWizard, setFinishWizard] = useState(false);

  useEffect(() => {
    setAllowScroll(false);
  }, [setAllowScroll]);

  useEffect(() => {
    if (doFinishWizard) {
      finishWizard();
      setFinishWizard(false);
    }
  }, [doFinishWizard]);

  const [localOrder, setLocalOrder] = useState<MaterialOrder>(order);

  const { data: routeInfo, isLoading: routeLoading } = useQuery<null | RouteInfo>({
    queryKey: [
      "routeInfo",
      {
        article: localOrder.article?.id || 0,
        position: localOrder.position?.id || 0,
        easyTarget: localOrder.easyTarget,
        job: jobId,
        jobPosition,
      },
    ],
    queryFn: ApiService.routeInfo(),
    enabled: true,
  });

  const setPositionField = (i: number, key: keyof TruckInfo | Partial<TruckInfo>, value?: any) => {
    const changes = typeof key === "string" ? { [key]: value } : key;
    setLocalOrder((s) => ({
      ...s,
      transportInformation: (s.transportInformation ?? []).map((t, xi) => ({
        ...t,
        ...(xi === i ? changes : {}),
      })),
    }));
  };

  const addPositionExpert = () => {
    setLocalOrder((s) => ({
      ...s,
      transportInformation: [
        ...(s.transportInformation ?? []),
        {
          type: "XX",
          amount: 0,
          thermo: false,
          siteWork: !isFreiBau ? s.amount === -1 : false,
          bordmatik: false,
          allrad: false,
          stahlmulde: false,
        },
      ],
    }));
  };

  const deletePosition = (i: number) => {
    setLocalOrder((s) => {
      const transportInformation = (s.transportInformation ?? []).slice();
      transportInformation.splice(i, 1);
      return {
        ...s,
        transportInformation,
        firstUnload: transportInformation.length > 0 ? s.firstUnload : null,
      };
    });
  };

  const hasSiteWork = useMemo(() => (localOrder.transportInformation ?? []).some((x) => !!x.siteWork), [localOrder.transportInformation]);

  const totalAmount = useMemo(
    () => (localOrder.transportInformation ?? []).reduce((carry, ti) => carry + (ti.amount ?? 0), 0),
    [localOrder.transportInformation],
  );

  const handleConfirm = () => {
    onSelect({
      transportInformation: localOrder.transportInformation?.filter((ti) => !(ti.type === "XX" || !ti.type || !ti.amount || ti.amount === 0)) ?? [],
      takt: localOrder.takt,
      duration: localOrder.duration,
    });
  };

  useEffect(() => {
    if (!expertMode) return;
    handleConfirm();
  }, [localOrder.takt, localOrder.duration, localOrder.transportInformation, expertMode]);

  const [taktAsked, setTaktAsked] = useState<boolean>(!autoCreate);
  const [isAutoCreated, setIsAutoCreated] = useState<boolean>(false);
  const [durationAsked, setDurationAsked] = useState<boolean>(localOrder.duration !== "INVALID");

  const [wizardStep, setWizardStep] = useState<WizardStep>("NONE");
  const [wizardIndex, setWizardIndex] = useState<number>(0);
  const [wizardMode, setWizardMode] = useState<WizardMode>(null);

  // Damit "Zurück" aus DURATION konsistent dahin führt, wo man herkam
  const [durationBackStep, setDurationBackStep] = useState<WizardStep>("TRUCK_SETUP");

  const transportInformation = localOrder.transportInformation ?? [];
  const currentTi: TruckInfo | undefined = transportInformation[wizardIndex];
  const hasArticle = !!localOrder.article;

  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (autoCreate && !expertMode) {
      autoStartedRef.current = true;
      startAddWizard(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCreate]);

  const startAddWizard = (iac: boolean = false) => {
    setLocalOrder((s) => {
      const list = [...(s.transportInformation ?? []), createDefaultTruckInfo(!!s.article, isFreiBau)];
      return { ...s, transportInformation: list };
    });
    setIsAutoCreated(iac === true);
    setWizardIndex((localOrder.transportInformation ?? []).length);
    setWizardMode("ADD");
    setWizardStep("TRUCK_SETUP");
  };

  const startEditWizard = (index: number) => {
    setIsAutoCreated(false);
    setWizardIndex(index);
    setWizardMode("EDIT");
    setWizardStep("TRUCK_SETUP");
  };

  const finishWizard = () => {
    setWizardStep("NONE");
    setWizardMode(null);
    if (isAutoCreated) {
      handleConfirm();
    }
  };

  const cleanupIfCancelledOnFirstStep = () => {
    if (wizardMode === "ADD") {
      setLocalOrder((s) => {
        const list = [...(s.transportInformation ?? [])];
        if (wizardIndex >= 0 && wizardIndex < list.length) {
          const ti = list[wizardIndex];
          if (ti.type === "XX" || !ti.type || !ti.amount || ti.amount === 0) {
            list.splice(wizardIndex, 1);
          }
        }
        return { ...s, transportInformation: list };
      });
    }
  };

  const canAskSiteWork = hasArticle && !isFreiBau;

  const goBackFromWizard = () => {
    if (wizardStep === "TRUCK_SETUP") {
      cleanupIfCancelledOnFirstStep();
      finishWizard();
      return;
    }

    switch (wizardStep) {
      case "TAKT":
        setWizardStep("TRUCK_SETUP");
        break;
      case "TRUCK_SITEWORK":
        setWizardStep("TRUCK_SETUP");
        break;
      case "DURATION":
        setWizardStep(durationBackStep);
        break;
      case "ANOTHER_TRUCK":
        if (canAskSiteWork) setWizardStep("TRUCK_SITEWORK");
        else setWizardStep("TRUCK_SETUP");
        break;
      default:
        finishWizard();
    }
  };

  const handleAnotherTruckAnswer = (another: boolean) => {
    if (another) {
      setLocalOrder((s) => {
        const list = [...(s.transportInformation ?? []), createDefaultTruckInfo(!!s.article, isFreiBau)];
        return { ...s, transportInformation: list };
      });
      setWizardIndex((idx) => idx + 1);
      setWizardMode("ADD");
      setWizardStep("TRUCK_SETUP");
    } else {
      finishWizard();
    }
  };

  const isBaustellendienst = order.amount === -1 && !order.article;
  const isBauhof = !!order.article && order.article.bauhof > 0;

  const summary = expertMode ? null : (
    <SummaryContainer>
      <MaterialSummary includeAmount order={order} isBauhof={isBauhof} isBaustellendienst={isBaustellendienst} />
      <UhrzeitSummary order={order} />
      {wizardStep !== "NONE" ? (
        <FuhrparkSummary order={localOrder} wizardIndex={["ANOTHER_TRUCK", "TAKT", "DURATION"].includes(wizardStep) ? -1 : wizardIndex} />
      ) : null}
    </SummaryContainer>
  );

  const renderWizard = () => {
    if (!currentTi) return null;

    const type = currentTi.type;

    const selectType = (newType: TruckInfo["type"]) => {
      setPositionField(wizardIndex, {
        type: newType,
        thermo: THERMO_TYPES.includes(newType) ? !!currentTi.thermo : false,
        bordmatik: BORDMATIK_TYPES.includes(newType) ? !!currentTi.bordmatik : false,
        stahlmulde: STAHLMULDE_TYPES.includes(newType) ? !!currentTi.stahlmulde : false,
        allrad: ALLRAD_TYPES.includes(newType) ? !!currentTi.allrad : false,
      });
    };

    const toggleEquip = (key: "thermo" | "bordmatik" | "stahlmulde" | "allrad") => {
      setPositionField(wizardIndex, key, !currentTi[key]);
    };

    const canThermo = THERMO_TYPES.includes(type);
    const canBordmatik = BORDMATIK_TYPES.includes(type);
    const canStahlmulde = STAHLMULDE_TYPES.includes(type);
    const canAllrad = ALLRAD_TYPES.includes(type);

    if (wizardStep === "TRUCK_SETUP") {
      const canContinue = (currentTi.amount ?? 0) >= 1 && currentTi.type !== "XX";

      const onContinue = () => {
        const autoSiteWork = !hasArticle;

        if (!hasArticle) {
          setPositionField(wizardIndex, "siteWork", true);
        }

        const shouldAskTakt = totalAmount > 1 && !taktAsked;
        if (shouldAskTakt) {
          setWizardStep("TAKT");
          return;
        }

        if (autoSiteWork && !durationAsked) {
          setDurationBackStep("TRUCK_SETUP");
          setWizardStep("DURATION");
          return;
        }

        if (canAskSiteWork) {
          setWizardStep("TRUCK_SITEWORK");
          return;
        }

        setWizardStep("ANOTHER_TRUCK");
      };

      return (
        <Box>
          {summary}

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{ fontSize: 18 }}>LKW festlegen</Typography>
          </Box>

          <Typography variant="button" sx={{ userSelect: "none" }}>
            Anzahl
          </Typography>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              disabled={!currentTi.amount || currentTi.amount <= 1}
              onClick={() => setPositionField(wizardIndex, "amount", !currentTi.amount ? null : currentTi.amount - 1 || null)}
            >
              <MinusIcon />
            </Button>

            <Typography sx={{ textAlign: "center", minWidth: 60, fontSize: 24 }}>{!currentTi.amount ? "0" : currentTi.amount}</Typography>

            <Button variant="contained" onClick={() => setPositionField(wizardIndex, "amount", !currentTi.amount ? 1 : currentTi.amount + 1)}>
              <PlusIcon />
            </Button>
          </Box>

          <Typography variant="button" sx={{ userSelect: "none" }}>
            LKW-Art
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 1, mb: 2 }}>
            {isAutoCreated ? <ActionItem label={"Kein LKW"} icon={Clear} big onPress={goBackFromWizard} /> : null}
            <ActionItem label="Sattel" icon={TruckIcon} big color={type === "SA" ? "blue" : undefined} onPress={() => selectType("SA")} />
            <ActionItem label="Solo" icon={TruckIcon} big color={type === "3A4A" ? "blue" : undefined} onPress={() => selectType("3A4A")} />
            <ActionItem label="3-Achser" big icon={TruckIcon} color={type === "3A" ? "blue" : undefined} onPress={() => selectType("3A")} />
            <ActionItem label="4-Achser" big icon={TruckIcon} color={type === "4A" ? "blue" : undefined} onPress={() => selectType("4A")} />
            <ActionItem label="2-Achser" icon={TruckIcon} color={type === "2A" ? "blue" : undefined} onPress={() => selectType("2A")} />
            <ActionItem label="Hängerzug" icon={TruckIcon} color={type === "HZ" ? "blue" : undefined} onPress={() => selectType("HZ")} />
            <ActionItem label="Tieflader" icon={TruckIcon} color={type === "TL" ? "blue" : undefined} onPress={() => selectType("TL")} />
            <ActionItem label="Kran-LKW" icon={TruckIcon} color={type === "KR" ? "blue" : undefined} onPress={() => selectType("KR")} />
          </Box>

          <Typography variant="button" sx={{ userSelect: "none" }}>
            Sonderausstattung
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 1, mb: 2 }}>
            <ActionItem
              big
              label="Thermo"
              icon={FlameIcon}
              color={currentTi.thermo ? "blue" : undefined}
              onPress={() => (canThermo ? toggleEquip("thermo") : null)}
              disabled={!canThermo}
            />
            <ActionItem
              big
              label="Bordmatik"
              icon={PanelLeftOpenIcon}
              color={currentTi.bordmatik ? "blue" : undefined}
              onPress={() => (canBordmatik ? toggleEquip("bordmatik") : null)}
              disabled={!canBordmatik}
            />
            <ActionItem
              big
              label="Stahlmulde"
              icon={ShieldIcon}
              color={currentTi.stahlmulde ? "blue" : undefined}
              onPress={() => (canStahlmulde ? toggleEquip("stahlmulde") : null)}
              disabled={!canStahlmulde}
            />
            <ActionItem
              big
              label="Allrad"
              icon={MountainSnowIcon}
              color={currentTi.allrad ? "blue" : undefined}
              onPress={() => (canAllrad ? toggleEquip("allrad") : null)}
              disabled={!canAllrad}
            />
          </Box>

          <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={goBackFromWizard} startIcon={<ArrowLeftIcon />}>
              Zurück
            </Button>

            <Button
              variant="contained"
              color="success"
              fullWidth
              disabled={!canContinue}
              onClick={onContinue}
              startIcon={<CheckIcon />}
              sx={{ opacity: canContinue ? 1 : 0.7 }}
            >
              Weiter
            </Button>
          </Box>
        </Box>
      );
    }

    if (wizardStep === "TRUCK_SITEWORK") {
      return (
        <Box>
          {summary}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{ fontSize: 18 }}>Danach Baustellendienst?</Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <ActionItem icon={ArrowLeftIcon} color={undefined} label="zurück" onPress={goBackFromWizard} />
            <ActionItem
              big
              icon={CheckIcon}
              label="Ja, Baustellendienst"
              color="default"
              onPress={() => {
                setPositionField(wizardIndex, "siteWork", true);
                if (!durationAsked) {
                  setDurationBackStep("TRUCK_SITEWORK");
                  setWizardStep("DURATION");
                } else {
                  setWizardStep("ANOTHER_TRUCK");
                }
              }}
            />
            <ActionItem
              big
              label="Nein"
              icon={XIcon}
              color="default"
              onPress={() => {
                setPositionField(wizardIndex, "siteWork", false);
                setWizardStep("ANOTHER_TRUCK");
              }}
            />
          </Box>
        </Box>
      );
    }

    if (wizardStep === "ANOTHER_TRUCK") {
      return (
        <Box>
          {summary}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{ fontSize: 18 }}>Fuhrpark fertig?</Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <ActionItem icon={ArrowLeftIcon} label="zurück" onPress={goBackFromWizard} />
            <ActionItem big color="default" icon={PlusIcon} label="Mehr" onPress={() => handleAnotherTruckAnswer(true)} />
            <ActionItem big label="Fertig" icon={CheckIcon} color="blue" onPress={() => handleAnotherTruckAnswer(false)} />
          </Box>
        </Box>
      );
    }

    if (wizardStep === "TAKT") {
      const goNextAfterTakt = () => {
        const autoSiteWork = !hasArticle;

        setTaktAsked(true);

        if (autoSiteWork && !durationAsked) {
          setDurationBackStep("TAKT");
          setWizardStep("DURATION");
          return;
        }

        if (canAskSiteWork) {
          setWizardStep("TRUCK_SITEWORK");
          return;
        }

        setWizardStep("ANOTHER_TRUCK");
      };

      return (
        <Box>
          {summary}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{ fontSize: 18 }}>Takt</Typography>
          </Box>

          <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Button
              variant="contained"
              disabled={localOrder.takt < 1}
              onClick={() =>
                setLocalOrder((s) => ({
                  ...s,
                  takt: Math.max(0, s.takt - 5),
                }))
              }
            >
              <MinusIcon />
            </Button>

            <Typography sx={{ textAlign: "center", minWidth: 140 }}>
              {localOrder.takt > 0 ? localOrder.takt + " min Abstand" : "alle gleichzeitig"}
            </Typography>

            <Button
              variant="contained"
              onClick={() =>
                setLocalOrder((s) => ({
                  ...s,
                  takt: s.takt + 5,
                }))
              }
            >
              <PlusIcon />
            </Button>
          </Box>

          <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={goBackFromWizard} startIcon={<ArrowLeftIcon />}>
              Zurück
            </Button>

            <Button variant="contained" color="success" fullWidth onClick={goNextAfterTakt} startIcon={<CheckIcon />} sx={{ opacity: 0.9 }}>
              Weiter
            </Button>
          </Box>
        </Box>
      );
    }

    if (wizardStep === "DURATION") {
      const setDuration = (duration: "HALF" | "FULL" | "TOUR") => {
        setLocalOrder((s) => ({
          ...s,
          duration,
        }));
        setDurationAsked(true);
        setWizardStep("ANOTHER_TRUCK");
      };

      return (
        <Box>
          {summary}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{ fontSize: 18 }}>LKW-Einsatzdauer</Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <ActionItem label="zurück" icon={ArrowLeftIcon} color={undefined} onPress={goBackFromWizard} />
            <ActionItem label="Ganzer Tag" icon={CircleCheckIcon} big color={undefined} onPress={() => setDuration("FULL")} />
            <ActionItem label="Halber Tag" icon={CircleSlashIcon} big color={undefined} onPress={() => setDuration("HALF")} />
            <ActionItem label="Eine Tour" icon={RotateCwIcon} big color={undefined} onPress={() => setDuration("TOUR")} />
          </Box>
        </Box>
      );
    }

    return null;
  };

  if (wizardStep !== "NONE") {
    return (
      <Box>
        {routeLoading ? <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 1 }} /> : null}
        {renderWizard()}
      </Box>
    );
  }

  return (
    <Box>
      {summary}

      {!expertMode && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography sx={{ fontSize: 18 }}>Fuhrpark</Typography>
        </Box>
      )}

      {routeLoading ? <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 1 }} /> : null}

      {expertMode ? (
        <Box sx={{ flex: 1, border: "1px solid", borderColor: "#aaa", mb: 1 }}>
          <Box>
            {(localOrder.transportInformation ?? []).length > 0 ? (
              (localOrder.transportInformation ?? []).map((ti, i) => (
                <TruckInformationRow
                  key={i}
                  routeInfo={routeInfo}
                  mainTakt={localOrder.takt}
                  expertMode={expertMode}
                  ti={ti}
                  setPositionField={setPositionField}
                  deletePosition={deletePosition}
                  i={i}
                />
              ))
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
                <Typography sx={{ color: "text.secondary" }}>Kein LKW benötigt (z.B. eigenständige Abholung)</Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", p: 0 }}>
              <IconButton size="small" onClick={addPositionExpert}>
                <PlusIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, borderRadius: 2, border: "1px solid", borderColor: "divider", mb: 1, p: 1 }}>
          {(localOrder.transportInformation ?? []).length > 0 && totalAmount > 0 ? (
            (localOrder.transportInformation ?? []).map((ti, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                  borderBottom: idx === (localOrder.transportInformation ?? []).length - 1 ? "none" : "1px solid",
                  borderBottomColor: "divider",
                  pb: 1,
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography>
                    {ti.amount ?? 0}x {truckTypeLabel(ti.type)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {[
                      ti.thermo ? "Thermo" : null,
                      ti.bordmatik ? "Bordmatik" : null,
                      ti.stahlmulde ? "Stahlmulde" : null,
                      ti.allrad ? "Allrad" : null,
                      ti.siteWork ? "Baustellendienst" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "ohne Zusatzausstattung"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton size="small" onClick={() => startEditWizard(idx)}>
                    <Edit3Icon />
                  </IconButton>
                  <IconButton size="small" onClick={() => deletePosition(idx)}>
                    <TrashIcon />
                  </IconButton>
                </Box>
              </Box>
            ))
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
              <Typography sx={{ color: "text.secondary" }}>Kein LKW benötigt (z.B. eigenständige Abholung)</Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0 }}>
            <IconButton size="small" onClick={() => startAddWizard()}>
              <PlusIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      {totalAmount > 0 ? (
        <Fragment>
          <Typography variant="button" sx={{ userSelect: "none" }}>
            Takt
          </Typography>

          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              disabled={localOrder.takt < 1}
              onClick={() =>
                setLocalOrder((s) => ({
                  ...s,
                  takt: Math.max(0, s.takt - 5),
                }))
              }
            >
              <MinusIcon />
            </Button>

            <Typography sx={{ textAlign: "center", minWidth: 120 }}>
              {localOrder.takt > 0 ? localOrder.takt + " min Abstand" : "alle gleichzeitig"}
            </Typography>

            <Button
              size="small"
              variant="contained"
              onClick={() =>
                setLocalOrder((s) => ({
                  ...s,
                  takt: s.takt + 5,
                }))
              }
            >
              <PlusIcon />
            </Button>
          </Box>

          {hasSiteWork ? (
            <Fragment>
              <TitleWithWarning warning={localOrder.duration === "INVALID"} text="LKW-Einsatzdauer" />

              <Box sx={{ overflowX: "auto", mb: 2 }}>
                <ToggleButtonGroup
                  value={localOrder.duration}
                  exclusive
                  onChange={(_, duration) => {
                    if (!duration) return;
                    setLocalOrder((s) => ({
                      ...s,
                      duration,
                    }));
                  }}
                  size="small"
                  sx={{ display: "inline-flex" }}
                >
                  <ToggleButton value="FULL">Ganzer Tag</ToggleButton>
                  <ToggleButton value="HALF">Halber Tag</ToggleButton>
                  <ToggleButton value="TOUR">Eine Tour</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Fragment>
          ) : null}
        </Fragment>
      ) : null}

      {!expertMode && (
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Button variant="contained" onClick={onBack} startIcon={<ArrowLeftIcon />}>
            Zurück
          </Button>

          <Button
            variant="contained"
            color="success"
            fullWidth
            onClick={handleConfirm}
            disabled={hasSiteWork && localOrder.duration === "INVALID"}
            sx={{ opacity: hasSiteWork && localOrder.duration === "INVALID" ? 0.7 : 1 }}
            startIcon={<CheckIcon />}
          >
            Weiter
          </Button>
        </Box>
      )}
    </Box>
  );
}
